import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, MailCheck } from "lucide-react";
import { SubPage, Spinner } from "../components/common";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";

function Card({ icon: Icon, title, sub, children }) {
  return (
    <div className="mx-auto max-w-md rounded-3xl bg-white p-6 shadow-[0_4px_20px_rgba(13,42,107,0.06)]">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10"><Icon className="h-6 w-6 text-brand" /></div>
      <h1 className="text-2xl font-extrabold text-navy">{title}</h1>
      {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try { await api.post("/auth/forgot-password", { email, lang }); setState("sent"); } catch { setState("idle"); }
  };
  return (
    <SubPage title={t("pw.forgotTitle")} testid="forgot-page">
      {state === "sent" ? (
        <Card icon={MailCheck} title={t("pw.forgotTitle")} sub={t("pw.sent")}>
          <Link to="/login" className="btn-primary w-full py-3.5" data-testid="forgot-back-login">{t("pw.backLogin")}</Link>
        </Card>
      ) : (
        <Card icon={KeyRound} title={t("pw.forgotTitle")} sub={t("pw.forgotSub")}>
          <form onSubmit={submit} className="space-y-3.5">
            <input className="field" type="email" required placeholder={t("auth.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
            <button className="btn-primary w-full py-3.5" disabled={state === "busy"} data-testid="forgot-submit-btn">
              {state === "busy" ? <Spinner className="border-white/40 border-t-white" /> : t("pw.sendLink")}
            </button>
          </form>
        </Card>
      )}
    </SubPage>
  );
}

export function ResetPasswordPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [state, setState] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError(t("pw.mismatch"));
    setError("");
    setState("busy");
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
      setState("done");
    } catch {
      setError(t("pw.invalid"));
      setState("idle");
    }
  };
  return (
    <SubPage title={t("pw.resetTitle")} testid="reset-page">
      {state === "done" ? (
        <Card icon={MailCheck} title={t("pw.done")}>
          <button className="btn-primary w-full py-3.5" onClick={() => navigate("/login", { replace: true })} data-testid="reset-go-login">{t("auth.login")}</button>
        </Card>
      ) : (
        <Card icon={KeyRound} title={t("pw.resetTitle")}>
          <form onSubmit={submit} className="space-y-3.5">
            <input className="field" type="password" minLength={6} required placeholder={t("set.newPw")} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password-input" />
            <input className="field" type="password" minLength={6} required placeholder={t("pw.repeat")} value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" data-testid="reset-password2-input" />
            {error && <p className="rounded-xl bg-alert/10 px-3 py-2 text-sm font-semibold text-alert" data-testid="reset-error">{error}</p>}
            <button className="btn-primary w-full py-3.5" disabled={state === "busy"} data-testid="reset-submit-btn">{t("pw.save")}</button>
          </form>
          {error && <Link to="/recuperar" className="mt-3 block text-center text-sm font-bold text-brand" data-testid="reset-new-link">{t("pw.sendLink")}</Link>}
        </Card>
      )}
    </SubPage>
  );
}
